import { GoogleGenAI, Type } from '@google/genai';
import { withRetry } from '../lib/retry';
import { GradingResponse, type GeometricData } from '../types';

function isRetryableGeminiError(e: unknown): boolean {
  if (e instanceof Error && e.name === 'AbortError') return false;
  const blob = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /\b(429|503|502|504|500)\b|RESOURCE_EXHAUSTED|UNAVAILABLE|ECONNRESET|ETIMEDOUT|overloaded|quota|rate limit|fetch failed|INTERNAL/i.test(
    blob
  );
}

type GeminiGenerateParams = Parameters<GoogleGenAI['models']['generateContent']>[0];
type GeminiGenerateResult = Awaited<ReturnType<GoogleGenAI['models']['generateContent']>>;

/** Vitest mocks @google/genai directly; browsers call server.js (never embed GEMINI_API_KEY). */
function usesDirectGeminiSdk(): boolean {
  if (typeof import.meta !== 'undefined' && import.meta.env.MODE === 'test') return true;
  return typeof window === 'undefined';
}

function getServerGeminiApiKey(): string {
  return readProcessEnv('GEMINI_API_KEY') || readProcessEnv('API_KEY') || '';
}

function geminiProxyUrl(): string {
  const base = (readBillingApiBase() ?? '').replace(/\/$/, '');
  return `${base}/api/ai/generate-content`;
}

async function geminiGenerateContent(request: GeminiGenerateParams): Promise<GeminiGenerateResult> {
  const run = async (): Promise<GeminiGenerateResult> => {
    if (usesDirectGeminiSdk()) {
      const apiKey = getServerGeminiApiKey();
      if (!apiKey) throw new Error('Missing Gemini API key');
      const ai = new GoogleGenAI({ apiKey });
      return ai.models.generateContent(request);
    }
    const res = await fetch(geminiProxyUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      throw new Error(`Gemini proxy HTTP ${res.status}`);
    }
    return (await res.json()) as GeminiGenerateResult;
  };
  return withRetry(run, {
    maxAttempts: 4,
    delayMs: 900,
    backoff: 'exponential',
    retryable: (err) => isRetryableGeminiError(err),
  });
}

/** Static env access only — dynamic `import.meta.env[key]` embeds every VITE_* secret. */
const readBillingApiBase = (): string | undefined => {
  if (typeof import.meta === 'undefined') return undefined;
  const v = import.meta.env.VITE_BILLING_API_BASE;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
};

const readProcessEnv = (key: string): string | undefined => {
  if (typeof process === 'undefined') return undefined;
  return process.env?.[key];
};

/**
 * Parse a JSON object from model text. Never throws.
 * Tries the full string first, then the first `{...}` slice (extra prose / fences).
 */
function parseModelJsonObject<T>(text: string): T | null {
  const raw = (text ?? '').trim();
  if (!raw) return null;
  const tryParse = (s: string): T | null => {
    try {
      return JSON.parse(s) as T;
    } catch {
      return null;
    }
  };
  const direct = tryParse(raw);
  if (direct !== null) return direct;
  const m = raw.match(/\{[\s\S]*\}/);
  if (!m) return null;
  return tryParse(m[0]);
}

export type FrameAssessmentResult = {
  scanHealth: number;
  alignment: 'IN_FRAME' | 'OVERLAP_DETECTED' | 'OUT_OF_BOUNDS';
  corners?: GeometricData;
  triggerSignal?: string;
  transcription?: string;
};

// Lightweight viewfinder assessment (no grading): scanHealth + corners + OCR text.
export const assessFrame = async (base64Image: string): Promise<FrameAssessmentResult | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          { inlineData: { mimeType: 'image/jpeg', data: base64Image } },
          {
            text: `You are an ultra-fast Document Viewfinder Sensor for teachers.

Return ONLY JSON.

## Goal
Assess whether a document is in-frame and readable enough to capture.

## Output rules
- scanHealth: 0-100 (penalize blur, glare, cutoff edges, low light).
- alignment: IN_FRAME / OVERLAP_DETECTED / OUT_OF_BOUNDS
- corners: 4 corners [x,y] (0-1000 scale) when visible
- transcription: OCR any visible handwriting/text (best-effort)
- triggerSignal: set to "SNAP" if scanHealth >= 90 and alignment is IN_FRAME`,
          },
        ],
      },
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            scanHealth: { type: Type.NUMBER },
            alignment: {
              type: Type.STRING,
              enum: ['IN_FRAME', 'OVERLAP_DETECTED', 'OUT_OF_BOUNDS'],
            },
            corners: {
              type: Type.OBJECT,
              properties: {
                topLeft: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                topRight: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                bottomLeft: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                bottomRight: { type: Type.ARRAY, items: { type: Type.NUMBER } },
              },
            },
            transcription: { type: Type.STRING },
            triggerSignal: { type: Type.STRING },
          },
          required: ['scanHealth', 'alignment'],
        },
      },
    });
    const text = response.text || '{}';
    return parseModelJsonObject<FrameAssessmentResult>(text);
  } catch (e) {
    console.error('Frame assessment error', e);
    return null;
  }
};

/** One row from a scanned paper schedule (bell, weekly grid, or monthly). */
export type PaperScheduleBlock = {
  title: string;
  day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
  startTime: string;
  endTime: string;
  location?: string;
  kind?: 'class' | 'prep' | 'lunch' | 'meeting' | 'pd' | 'other';
  /** 0–1 how sure the model is this row matches the page. */
  confidence: number;
  /** If true, the teacher must explicitly confirm this row before import. */
  needsUserConfirmation: boolean;
  uncertaintyNote?: string;
  suggestedRecurrence: 'once' | 'weekly' | 'monthly';
};

export type PaperScheduleImportResult = {
  /** Short description of what the model saw (e.g. "5-day period grid"). */
  layoutDescription: string;
  /** Non-empty when the model needs the teacher to resolve ambiguity (dates, rotation, etc.). */
  globalQuestions: string[];
  blocks: PaperScheduleBlock[];
};

/**
 * Parse a photo of a printed or handwritten school schedule for import.
 * Teachers must still confirm rows in the UI — models can misread images.
 */
export const parsePaperScheduleForImport = async (
  base64Image: string
): Promise<PaperScheduleImportResult | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Image,
            },
          },
          {
            text: `You extract school schedules from a photo for a teacher calendar app.

Return ONLY JSON matching the schema. No markdown.

## Accuracy rules
- Extract EVERY visible period/block as its own row (period-by-period for bell schedules; one row per cell for week grids).
- Times: 24-hour HH:MM. If AM/PM is ambiguous, pick the most likely school-day interpretation BUT set needsUserConfirmation true and explain in uncertaintyNote.
- day must be Mon–Sun for that block’s weekday column (or the day label on the page).
- suggestedRecurrence:
  - "weekly" for repeating bell schedules, cycle days, or week grids that repeat each week.
  - "monthly" for month-at-a-glance layouts or explicit monthly patterns.
  - "once" for a single dated event with no repeat implied.
- confidence: 0.0–1.0 for this row (lower if handwriting, blur, crop, or conflicting text).
- needsUserConfirmation: true if ANY field might be wrong (illegible, guessed day, guessed week, missing end time estimated, etc.).
- uncertaintyNote: short reason when needsUserConfirmation is true.

## globalQuestions
Add 1–5 short questions the app will show the teacher when something is unclear (e.g. "Which calendar week does this Monday column refer to?" or "Is Period 4 lunch or a class?"). Use [] if everything is clear.

## layoutDescription
One sentence describing the layout (e.g. "Weekly grid Mon–Fri with 8 periods" or "Single-day bell schedule").

If there is no schedule in the image, return blocks: [] and explain in layoutDescription.`,
          },
        ],
      },
      config: {
        thinkingConfig: { thinkingBudget: 2048 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            layoutDescription: { type: Type.STRING },
            globalQuestions: { type: Type.ARRAY, items: { type: Type.STRING } },
            blocks: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  title: { type: Type.STRING },
                  day: {
                    type: Type.STRING,
                    enum: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                  },
                  startTime: { type: Type.STRING },
                  endTime: { type: Type.STRING },
                  location: { type: Type.STRING },
                  kind: {
                    type: Type.STRING,
                    enum: ['class', 'prep', 'lunch', 'meeting', 'pd', 'other'],
                  },
                  confidence: { type: Type.NUMBER },
                  needsUserConfirmation: { type: Type.BOOLEAN },
                  uncertaintyNote: { type: Type.STRING },
                  suggestedRecurrence: {
                    type: Type.STRING,
                    enum: ['once', 'weekly', 'monthly'],
                  },
                },
                required: [
                  'title',
                  'day',
                  'startTime',
                  'endTime',
                  'confidence',
                  'needsUserConfirmation',
                  'suggestedRecurrence',
                ],
              },
            },
          },
          required: ['layoutDescription', 'globalQuestions', 'blocks'],
        },
      },
    });
    const text = response.text || '{}';
    const parsed = parseModelJsonObject<PaperScheduleImportResult>(text);
    if (!parsed || !parsed.blocks || !Array.isArray(parsed.blocks)) return null;
    parsed.globalQuestions = Array.isArray(parsed.globalQuestions)
      ? parsed.globalQuestions.filter((q) => typeof q === 'string' && q.trim())
      : [];
    parsed.layoutDescription =
      typeof parsed.layoutDescription === 'string' ? parsed.layoutDescription : '';
    return parsed;
  } catch (e) {
    console.error('Paper schedule import parse error', e);
    return null;
  }
};

// Service to extract rubric text from an image using Gemini
export const extractRubricFromImage = async (base64Image: string): Promise<string | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Image,
            },
          },
          {
            text: `You are an expert educational administrator. Extract all the grading criteria, point distributions, and expectations from this rubric image as clear text. Organize it logically. If there is no readable text or it is clearly not a rubric, reply with exactly "NO RUBRIC".`,
          },
        ],
      },
    });
    return response.text || null;
  } catch (e) {
    console.error('Rubric extraction error', e);
    return null;
  }
};

// Service to generate a rubric from a title and description
export const generateRubric = async (
  assignmentTitle: string,
  assignmentDescription: string,
  maxScore: number
): Promise<string | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `Generate a professional and detailed grading rubric for: ${assignmentTitle}. 
      Context: ${assignmentDescription}. 
      Total points: ${maxScore}. 
      Return only the formatted rubric text.`,
    });
    return response.text || null;
  } catch (e) {
    console.error('Rubric generation error', e);
    return null;
  }
};

// Service to analyze a student's work against a rubric
export const analyzePaper = async (
  base64Image: string,
  rubric: string,
  maxScore: number,
  studentList: string[],
  _isAutoDetect: boolean = false
): Promise<GradingResponse | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Image,
            },
          },
          {
            text: `You are an ultra-fast Document Intelligence Sensor for teachers.

## MISSION
Real-time document detection, OCR, and pedagogical assessment.

## Phase 1: Viewfinder Assessment
Active Zone: 10-90% frame. 
- Assessment: 'scanHealth' (0-100). Subtract points heavily for blurriness, cutoff edges, or poor lighting.
- Alignment: 'alignment' (IN_FRAME / OVERLAP_DETECTED / OUT_OF_BOUNDS).

## Phase 2: High-Speed Capture
- TRIGGER: If 'scanHealth' > 92%, set 'triggerSignal' to "SNAP".
- Coordinates: Provide 'corners' coordinates [x,y] for 4 corners (Scale 0-1000).

## Phase 3: Personalized Pedagogical Analysis (Only if health > 92%)
- Match student from: ${studentList.join(', ')}.
- OCR: Extract handwriting carefully.
- SCORE: Evaluate against Rubric: ${rubric} (Max: ${maxScore}).
- CRITICAL GRADING ADJUSTMENTS:
  1. Name Check: You MUST check if the student wrote their name. Deduct points if missing, award/maintain if present.
  2. Effort & Clarity: Adjust the score positively for visible effort and logically clear answers.
  3. Handwriting: Adjust the score based on the neatness and legibility of the handwriting.
- FEEDBACK RULES (MANDATORY): 
  1. DO NOT use any formal greetings or salutations (e.g., NO "Hi", "Hello", "Dear").
  2. You MUST ALWAYS include the student's first name (the first word of their full name, or the only word if applicable) naturally within the feedback text.
  3. Start directly with the evaluation of the work.
  4. Tone MUST be realistic, professional, and highly encouraging.
  5. Content: Use the second person ("you", "your") throughout. Be specific about strengths and provide one clear, constructive path for improvement.
  6. Explicitly mention the student's handwriting, effort, or name inclusion in the feedback to explain their score.
  7. Example: "John, your analysis of the plant cell structure is excellent, and your neat handwriting makes it very easy to read! To improve, try to label the cell wall more clearly next time. Great effort!"

Return ONLY JSON.`,
          },
        ],
      },
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            detected: { type: Type.BOOLEAN },
            scanHealth: { type: Type.NUMBER },
            alignment: {
              type: Type.STRING,
              enum: ['IN_FRAME', 'OVERLAP_DETECTED', 'OUT_OF_BOUNDS'],
            },
            corners: {
              type: Type.OBJECT,
              properties: {
                topLeft: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                topRight: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                bottomLeft: { type: Type.ARRAY, items: { type: Type.NUMBER } },
                bottomRight: { type: Type.ARRAY, items: { type: Type.NUMBER } },
              },
            },
            triggerSignal: { type: Type.STRING },
            oneWordCommand: { type: Type.STRING },
            studentName: { type: Type.STRING },
            score: { type: Type.NUMBER },
            feedback: { type: Type.STRING },
            transcription: { type: Type.STRING },
          },
          required: ['detected', 'scanHealth', 'alignment'],
        },
      },
    });

    const text = response.text || '{}';
    return parseModelJsonObject<GradingResponse>(text);
  } catch (e) {
    console.error('Gemini analysis error', e);
    return null;
  }
};

export const analyzeMultiPagePaper = async (
  base64Images: string[],
  rubric: string,
  maxScore: number,
  studentList: string[]
): Promise<GradingResponse | null> => {
  try {
    const imageParts = base64Images
      .filter(Boolean)
      .slice(0, 10)
      .map((data) => ({
        inlineData: {
          mimeType: 'image/jpeg',
          data,
        },
      }));

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          ...imageParts,
          {
            text: `You are grading ONE student's work that may span multiple pages.

## Instructions
- Consider ALL pages as a single submission.
- Match the student from: ${studentList.join(', ')}.
- OCR handwriting carefully across pages.
- SCORE: Evaluate against Rubric: ${rubric} (Max: ${maxScore}).
- Provide ONE final score and ONE consolidated feedback.

## FEEDBACK RULES (MANDATORY)
1. DO NOT use greetings/salutations.
2. Always include the student's first name naturally.
3. Start directly with the evaluation.
4. Be encouraging and specific; provide one clear improvement path.
5. Mention effort/handwriting/name inclusion to justify the score.

Return ONLY JSON.`,
          },
        ],
      },
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            detected: { type: Type.BOOLEAN },
            studentName: { type: Type.STRING },
            score: { type: Type.NUMBER },
            feedback: { type: Type.STRING },
            transcription: { type: Type.STRING },
          },
          required: ['detected'],
        },
      },
    });

    const text = response.text || '{}';
    return parseModelJsonObject<GradingResponse>(text);
  } catch (e) {
    console.error('Gemini multi-page analysis error', e);
    return null;
  }
};

export interface LessonScriptResult {
  outline: string;
  vocabulary: string[];
  discussionQuestions: string[];
}

export type LessonPartKey = 'doNow' | 'iDo' | 'weDo' | 'youDo' | 'exitTicket';

export const generateLessonScript = async (topic: string): Promise<LessonScriptResult | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are an expert K-12 curriculum designer. For this topic: "${topic}", provide a 30-minute lesson plan.

Return ONLY valid JSON with exactly these keys (no markdown, no extra text):
- "outline": string with a step-by-step 30-minute lesson outline (numbered steps, brief).
- "vocabulary": array of 5-10 key vocabulary words or phrases students should learn.
- "discussionQuestions": array of exactly 3 discussion questions for the class.`,
      config: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            outline: { type: Type.STRING },
            vocabulary: { type: Type.ARRAY, items: { type: Type.STRING } },
            discussionQuestions: { type: Type.ARRAY, items: { type: Type.STRING } },
          },
          required: ['outline', 'vocabulary', 'discussionQuestions'],
        },
      },
    });
    const text = response.text || '{}';
    return parseModelJsonObject<LessonScriptResult>(text);
  } catch (e) {
    console.error('Lesson script error', e);
    return null;
  }
};

export const generateLessonPartChoices = async (params: {
  objective: string;
  part: LessonPartKey;
  grade: string;
  subject: string;
  topic?: string;
}): Promise<string[] | null> => {
  try {
    const { objective, part, grade, subject, topic } = params;

    const partGuidance: Record<LessonPartKey, string> = {
      doNow:
        'Create a 2–3 minute warm-up (Do Now) aligned to the objective. Provide text a teacher can paste into a student-facing or teacher-facing prompt.',
      iDo: 'Create mini-lesson direct instruction text (I do). Provide concise step prompts a teacher can model/think-aloud.',
      weDo: 'Create guided practice text (We do). Provide collaborative steps and quick check prompts.',
      youDo:
        'Create independent practice text (You do). Provide task directions and a success checklist sentence.',
      exitTicket:
        'Create an exit ticket prompt a teacher can paste. It should support checking the objective (3 quick prompts if possible, but keep as one prompt text).',
    };

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are an expert K-12 curriculum designer.

Objective (teacher-written learning target):
"${objective}"

Grade: ${grade}
Subject: ${subject}
${topic ? `Topic context: ${topic}` : ''}

Task:
Generate EXACTLY 3 distinct options for the lesson part: "${part}".

Rules:
- Return ONLY valid JSON.
- JSON must have exactly one key: "choices"
- "choices" must be an array of exactly 3 strings.
- Each string must be concise, classroom-ready, and aligned to the objective.
- Do NOT include markdown, code fences, or explanations.

Part guidance:
${partGuidance[part]}`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            choices: { type: Type.ARRAY, items: { type: Type.STRING } },
          },
          required: ['choices'],
        },
      },
    });

    const text = response.text || '{}';
    const parsed = parseModelJsonObject<{ choices: string[] }>(text);
    if (!parsed || !Array.isArray(parsed.choices)) return null;
    return parsed.choices.slice(0, 3);
  } catch (e) {
    console.error('Lesson part choices generation error', e);
    return null;
  }
};

export const generateLearningTargetAndSuccessCriteria = async (params: {
  lessonTitle: string;
  grade: string;
  subject: string;
  objectiveHint?: string;
}): Promise<{ objective: string; studentSuccessCriteria: string } | null> => {
  try {
    const { lessonTitle, grade, subject, objectiveHint } = params;

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are an expert K-12 curriculum designer.

Create:
1) A teacher-written learning target (objective) that starts with exactly: "I can ..."
2) A student-facing success criteria statement that starts with exactly: "You know you are successful when ..."

Inputs:
- Lesson title: ${lessonTitle}
- Grade: ${grade}
- Subject: ${subject}
- Objective hint (optional): ${objectiveHint || 'N/A'}

Rules:
- Objective must be 1 sentence and directly measurable/aligned to the lesson title.
- Student success criteria must be 1–2 sentences that students can use to self-check.
- Return ONLY valid JSON with exactly these keys (no markdown):
  - objective: string
  - studentSuccessCriteria: string
`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            objective: { type: Type.STRING },
            studentSuccessCriteria: { type: Type.STRING },
          },
          required: ['objective', 'studentSuccessCriteria'],
        },
      },
    });

    const text = response.text || '{}';
    return parseModelJsonObject<{ objective: string; studentSuccessCriteria: string }>(text);
  } catch (e) {
    console.error('Learning target generation error', e);
    return null;
  }
};

export const generatePlanningContext = async (params: {
  lessonTitle: string;
  gradeHint?: string;
  subjectHint?: string;
  learningTargetHint?: string;
}): Promise<{ grade: string; subject: string; duration: number } | null> => {
  try {
    const { lessonTitle, gradeHint, subjectHint, learningTargetHint } = params;

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are an expert K-12 curriculum designer.

Given:
- Lesson title: ${lessonTitle}
- Learning target hint (optional): ${learningTargetHint || 'N/A'}
- Grade hint (optional): ${gradeHint || 'N/A'}
- Subject hint (optional): ${subjectHint || 'N/A'}

Task:
Return a best-fit planning context for this lesson:
- grade: either one of K, 1, 2, ... 12 OR any grade-like string (e.g., "3.5") if no exact match fits.
- subject: a concise subject name (e.g., "Science", "Math", "Language Arts", etc.) OR a custom subject string.
- duration: a number of minutes suitable for this lesson (between 10 and 120).

Rules:
- Return ONLY valid JSON with exactly these keys: grade, subject, duration.
- No markdown, no extra keys.`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            grade: { type: Type.STRING },
            subject: { type: Type.STRING },
            duration: { type: Type.NUMBER },
          },
          required: ['grade', 'subject', 'duration'],
        },
      },
    });

    const text = response.text || '{}';
    const parsed = parseModelJsonObject<{ grade: string; subject: string; duration: number }>(text);
    if (!parsed?.grade || !parsed.subject) return null;
    const d = Number(parsed.duration);
    if (!Number.isFinite(d)) return null;
    return {
      grade: parsed.grade,
      subject: parsed.subject,
      duration: Math.max(10, Math.min(120, Math.round(d))),
    };
  } catch (e) {
    console.error('Planning context generation error', e);
    return null;
  }
};

export const generateDifferentiatedLesson = async (
  lessonText: string,
  level: 'simplified' | 'advanced'
): Promise<string | null> => {
  const directive =
    level === 'simplified'
      ? 'Rewrite this lesson for students with learning gaps: shorter sentences, simpler vocabulary, more scaffolding, and one extra practice step. Keep the same learning goal.'
      : 'Rewrite this lesson for advanced/gifted students: add depth, extension questions, and one enrichment task. Keep the same learning goal.';
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `${directive}\n\nLesson:\n${lessonText}`,
    });
    return response.text || null;
  } catch (e) {
    console.error('Differentiation error', e);
    return null;
  }
};

export const translateText = async (
  text: string,
  targetLanguage: 'es' | 'en'
): Promise<string | null> => {
  try {
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `Translate this text to ${targetLanguage === 'es' ? 'Spanish' : 'English'}.
Return ONLY the translated text (no quotes, no markdown).

Text:
${text}`,
      config: { thinkingConfig: { thinkingBudget: 0 } },
    });
    return (response.text || '').trim() || null;
  } catch (e) {
    console.error('Translation error', e);
    return null;
  }
};

/** System prompt for Lesson Architect module generation (admin-ready). */
export const LESSON_ARCHITECT_SYSTEM_PROMPT = `You are the Lead Instructional Consultant for a Blue Ribbon School District. Your task is to generate lesson components that are Audit-Proof and Pedagogically Elite.

Instructions for Generation:
Cross-Modular Awareness: If the 'Independent Practice' is selected as 'Choice Board,' ensure the 'Assessment' section includes a rubric that fits all choices on that board.
Linguistic Scaffolding: If ELL is selected in the Sidebar, you must provide a 'Word Wall' list and 'Sentence Frames' for the specific academic vocabulary used in that section.
The 'Admin' Standard: Write in a professional, objective-driven tone. Use Bloom's Taxonomy verbs (Analyze, Evaluate, Synthesize) instead of 'Understand' or 'Learn.'
Physical Feasibility: If the teacher selected 'No Tech,' do not suggest Kahoots or Google Slides. Suggest physical movement and paper-based scaffolds.`;

export type LessonArchitectAction = 'generate' | 'differentiate_iep' | 'increase_rigor';

export type StandardsSearchHit = {
  code: string;
  label: string;
  frameworkGuess: string;
};

export const searchStandardsForLessonArchitect = async (params: {
  query: string;
  jurisdiction: string;
  grade: string;
  subject: string;
}): Promise<StandardsSearchHit[] | null> => {
  try {
    const { query, jurisdiction, grade, subject } = params;
    if (!query.trim()) return [];

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You help teachers find standards. Suggest up to 8 plausible matches for the teacher's search.

Jurisdiction / state: ${jurisdiction}
Grade: ${grade}
Subject: ${subject}
Search text: ${query.trim()}

Return ONLY valid JSON with one key "items" — an array of objects with:
- code: short standard code (e.g. CCSS, NGSS, or state-style)
- label: one-sentence description
- frameworkGuess: "CCSS" | "NGSS" | "State" | "Other"

Do not include markdown or commentary.`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            items: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  code: { type: Type.STRING },
                  label: { type: Type.STRING },
                  frameworkGuess: { type: Type.STRING },
                },
                required: ['code', 'label', 'frameworkGuess'],
              },
            },
          },
          required: ['items'],
        },
      },
    });

    const text = response.text || '{}';
    const parsed = parseModelJsonObject<{ items: StandardsSearchHit[] }>(text);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return parsed.items.slice(0, 8);
  } catch (e) {
    console.error('Standards search error', e);
    return null;
  }
};

export const generateLessonArchitectAction = async (params: {
  action: LessonArchitectAction;
  targetModuleId: string;
  fullStateJson: string;
}): Promise<string | null> => {
  try {
    const { action, targetModuleId, fullStateJson } = params;

    const actionLine =
      action === 'generate'
        ? 'Generate the primary section content for the target module.'
        : action === 'differentiate_iep'
          ? 'Rewrite this module with strong IEP/504 accommodations, chunked directions, and multiple means of expression while preserving rigor.'
          : 'Increase rigor using Bloom verbs (Analyze, Evaluate, Synthesize); add extension prompts and evidence expectations.';

    const userPayload = `FULL_LESSON_STATE_JSON:\n${fullStateJson}\n\nTARGET_MODULE_ID: ${targetModuleId}\nACTION: ${action}\n\n${actionLine}\n\nOutput ONLY the markdown body for this single module (teacher-facing, ready to paste). Use clear headings and bullets. Do not wrap in JSON.`;

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `${LESSON_ARCHITECT_SYSTEM_PROMPT}\n\n---\n\n${userPayload}`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            markdown: { type: Type.STRING },
          },
          required: ['markdown'],
        },
      },
    });

    const text = response.text || '{}';
    const parsed = parseModelJsonObject<{ markdown: string }>(text);
    if (!parsed) return (text || '').trim() || null;
    const md = (parsed.markdown || '').trim();
    return md || null;
  } catch (e) {
    console.error('Lesson architect generation error', e);
    return null;
  }
};

/** Single Class DNA text field the teacher wants AI to draft from surrounding context. */
export type LessonArchitectDnaAiFillField =
  | 'lessonTitle'
  | 'lessonTopic'
  | 'planObjective'
  | 'planStudentSuccessCriteria'
  | 'planPrepMaterials'
  | 'reflectionNote'
  | 'resourceQuery';

export type LessonArchitectFieldFillTarget =
  | { type: 'dna'; field: LessonArchitectDnaAiFillField }
  | { type: 'module_seed'; moduleId: string };

const DNA_FIELD_INSTRUCTIONS: Record<LessonArchitectDnaAiFillField, string> = {
  lessonTitle:
    'Write a concise, engaging lesson title (5–12 words). No quotes. Match grade/subject and any learning objective.',
  lessonTopic:
    'Write a short unit or topic line (1–2 sentences) that situates the lesson. Plain text only.',
  planObjective:
    'Write exactly one measurable learning objective that starts with "I can ...". Align to title, grade, subject, standards, and duration.',
  planStudentSuccessCriteria:
    'Write 1–2 student-facing sentences starting with "You know you are successful when ...". Plain, observable criteria.',
  planPrepMaterials:
    'List materials and teacher prep as a short plain-text checklist (bullets or lines). Match duration, tech, and objective.',
  reflectionNote:
    'Write 2–4 brief post-lesson reflection prompts a teacher could answer (plain text, teacher-facing).',
  resourceQuery:
    'Write a short search phrase (3–8 words) to find videos, articles, or simulations for this lesson. No URLs.',
};

/**
 * Fill one Class DNA field or a module "seed" using the full lesson state JSON.
 */
export const generateLessonArchitectFieldFill = async (params: {
  target: LessonArchitectFieldFillTarget;
  fullStateJson: string;
  /** e.g. "Hook (Engagement)" when target is module_seed */
  moduleHumanLabel?: string;
}): Promise<string | null> => {
  try {
    const { target, fullStateJson, moduleHumanLabel } = params;

    let taskBlock: string;
    if (target.type === 'dna') {
      taskBlock = `TARGET: Class DNA field "${target.field}"\nINSTRUCTIONS: ${DNA_FIELD_INSTRUCTIONS[target.field]}`;
    } else {
      const label = moduleHumanLabel || target.moduleId;
      taskBlock = `TARGET: Module seed for "${target.moduleId}" (${label})\nINSTRUCTIONS: Write a short "rough idea" seed (2–5 sentences) for this lesson phase only. Align with the selected strategy for this module, grade, subject, objective, and learner profiles. Do not write full lesson script — only angles, constraints, or hooks the teacher can refine. Plain text, no markdown fences.`;
    }

    const userPayload = `FULL_LESSON_STATE_JSON:\n${fullStateJson}\n\n${taskBlock}\n\nReturn ONLY valid JSON with one key "value" (string). The string is the complete field content. No markdown wrapper, no extra keys.`;

    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `${LESSON_ARCHITECT_SYSTEM_PROMPT}\n\n---\n\n${userPayload}`,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            value: { type: Type.STRING },
          },
          required: ['value'],
        },
      },
    });

    const text = response.text || '{}';
    const parsed = parseModelJsonObject<{ value: string }>(text);
    if (!parsed) return (text || '').trim() || null;
    const v = (parsed.value || '').trim();
    return v || null;
  } catch (e) {
    console.error('Lesson architect field fill error', e);
    return null;
  }
};

/** Short, practical coaching answer for the Teach tab assistant (under ~200 words). */
export const quickTeachAssist = async (question: string): Promise<string | null> => {
  try {
    if (!question.trim()) return null;
    const response = await geminiGenerateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are a concise K-12 teaching coach. Reply in plain text, max ~180 words, short bullets or numbered steps. No greeting. Be concrete and classroom-ready.

Question:
${question.trim()}`,
      config: { thinkingConfig: { thinkingBudget: 0 } },
    });
    const t = (response.text || '').trim();
    return t || null;
  } catch (e) {
    console.error('Teach assist error', e);
    return null;
  }
};
